// Incremental parser entrypoint for top-level segment reuse.
// Falls back to full parser when scanner cannot segment source safely.
import type { LuisArrayVariables, LuisNode, LuisObjectVariables } from '../../types'
import { createStableHash } from '../../incremental/hash'
import { scanTopLevelLuisSegments } from './segments'
import { createObjectVariablesSignature } from './object-signature'
import { buildPreviousFingerprintMap, resolveReusableSegment } from './cache'
import { parseOneSegment, buildFallbackParseOutput } from './parse-segment'
import { applyParsedSegment, cloneLuisNode } from './tree'
import type { LuisIncrementalParserCache, LuisIncrementalParserOutput, LuisParsedSegmentCache } from './types'

export type {
  LuisScannedSegment,
  LuisIncrementalParserCache,
  LuisIncrementalParserStats,
  LuisIncrementalParserOutput,
} from './types'

// Parse source with top-level segment reuse where fingerprints are unchanged.
export function parseLuisIncremental(
  source: string,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables = {},
  previousCache?: LuisIncrementalParserCache,
): LuisIncrementalParserOutput {
  const objectSignature = createObjectVariablesSignature(objectVariables, arrayVariables)
  // Use strict full parser on cold start to skip incremental scanner overhead.
  if (!previousCache) {
    // Return fallback parse output for first compile cycle.
    return buildFallbackParseOutput(source, objectVariables, arrayVariables, objectSignature)
  } else {
    // Continue incremental parse flow when cache exists.
  }

  const scannedSegments = scanTopLevelLuisSegments(source)
  // Fall back to full parser when scanner cannot safely segment source.
  if (!scannedSegments) {
    // Return fallback parse output for safe correctness.
    return buildFallbackParseOutput(source, objectVariables, arrayVariables, objectSignature)
  } else {
    // Continue incremental segment parsing.
  }

  const root: LuisNode = { type: 'root', value: '', line: 1, declarations: [], children: [] }
  const previousByKey = new Map(previousCache?.segments.map((segment) => [segment.key, segment]) ?? [])
  const previousByFingerprint = buildPreviousFingerprintMap(previousCache, objectSignature)
  const usedFingerprintSegments = new Set<LuisParsedSegmentCache>()
  const nextSegments: LuisParsedSegmentCache[] = []
  let reusedSegments = 0

  // Parse or reuse each scanned top-level segment in source order.
  for (const scannedSegment of scannedSegments) {
    const fingerprint = createStableHash(scannedSegment.content)
    const reused = resolveReusableSegment(
      scannedSegment,
      fingerprint,
      previousByKey,
      previousByFingerprint,
      usedFingerprintSegments,
    )

    // Reuse cached parsed segment when fingerprints still match.
    if (reused) {
      reusedSegments += 1
      nextSegments.push({
        key: scannedSegment.key,
        kind: reused.kind,
        fingerprint: reused.fingerprint,
        declarations: [...reused.declarations],
        children: reused.children.map((child) => cloneLuisNode(child)),
      })
      applyParsedSegment(root, reused.declarations, reused.children)
      continue
    } else {
      // Continue with fresh segment parse.
    }

    const parsedSegment = parseOneSegment(scannedSegment, objectVariables, arrayVariables)
    nextSegments.push({
      key: scannedSegment.key,
      kind: scannedSegment.kind,
      fingerprint,
      declarations: [...parsedSegment.declarations],
      children: parsedSegment.children.map((child) => cloneLuisNode(child)),
    })
    applyParsedSegment(root, parsedSegment.declarations, parsedSegment.children)
  }

  // Return incremental parser result with next cache snapshot.
  return {
    root,
    cache: {
      objectSignature,
      segments: nextSegments,
    },
    stats: {
      totalSegments: scannedSegments.length,
      reusedSegments,
      parsedSegments: scannedSegments.length - reusedSegments,
    },
  }
}
